/**
 * Content card gradient colors — shared by OG images, digest email, and platform cards.
 * Keep in sync with `logictry_server/utils/contentGradient.js`.
 */

/** Deterministic hue from a seed (id/title) so image-less cards get a stable color. */
export function hashHue(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

export function gradientFromHsl(h, s, l) {
  const h2 = (h + 35) % 360;
  const s2 = Math.max(0, s - 4);
  const l2 = Math.max(0, l - 12);
  return `linear-gradient(135deg, hsl(${h}, ${s}%, ${l}%) 0%, hsl(${h2}, ${s2}%, ${l2}%) 100%)`;
}

/** Parse #rgb / #rrggbb to {h,s,l}. Returns null for unsupported formats. */
export function hexToHsl(hex) {
  if (typeof hex !== 'string') return null;
  const m = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue *= 60;
  }
  return { h: Math.round(hue), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/** Build a gradient from a user-specified color, mirroring the fallback strategy. */
export function gradientFromColor(color) {
  const hsl = hexToHsl(color);
  if (!hsl) return color;
  return gradientFromHsl(hsl.h, hsl.s, hsl.l);
}

export function fallbackGradient(seed) {
  const hue = hashHue(seed);
  return gradientFromHsl(hue, 62, 46);
}
